import { useMutation } from '@tanstack/react-query';
import { type DragEvent, useRef, useState } from 'react';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Select } from '../../../components/ui/FormField.js';
import { api } from '../../../services/api.js';

/**
 * 098 (T007-T009, T018-T019): one photo as the car's `images` array carries it. T007-T009
 * shipped upload + a plain thumbnail list. This extension (T018-T019) adds per-thumbnail
 * controls — make-primary, category select, delete — and native HTML5 drag-to-reorder.
 */
export interface CarPhotoImage {
  file_asset_id: string;
  url?: string;
  is_primary?: boolean;
  sort_order: number;
  category?: string;
}

interface CarPhotosPanelProps {
  carId: string;
  images: CarPhotoImage[];
  onChange: () => void;
}

const CATEGORY_OPTIONS = ['exterior', 'interior', 'damage', 'other'] as const;

export function CarPhotosPanel({ carId, images, onChange }: CarPhotosPanelProps) {
  const [file, setFile] = useState<File | null>(null);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());
  const [isDropActive, setIsDropActive] = useState(false);
  const dragIndex = useRef<number | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (toUpload: File) => {
      // Same two-call pattern as SupplierProfilePage's logo upload: upload the binary for an
      // asset id, then a second request that actually attaches it to the record.
      const fd = new FormData();
      fd.append('file', toUpload);
      fd.append('purpose', 'car_image');
      const asset = (await api.upload('/files/upload', fd)) as { id: string };
      return api.post(`/cars/${carId}/images`, { file_asset_id: asset.id });
    },
    onSuccess: () => {
      setFile(null);
      onChange();
    },
  });

  const primaryMutation = useMutation({
    mutationFn: (imageId: string) =>
      api.patch(`/cars/${carId}/images/${imageId}`, { is_primary: true }),
    onSuccess: onChange,
  });

  const categoryMutation = useMutation({
    mutationFn: ({ imageId, category }: { imageId: string; category: string }) =>
      api.patch(`/cars/${carId}/images/${imageId}`, { category }),
    onSuccess: onChange,
  });

  const deleteMutation = useMutation({
    mutationFn: (imageId: string) => api.delete(`/cars/${carId}/images/${imageId}`),
    onSuccess: onChange,
  });

  const reorderMutation = useMutation({
    mutationFn: (order: string[]) => api.put(`/cars/${carId}/images/order`, { order }),
    onSuccess: onChange,
  });

  function handleUpload() {
    if (file) uploadMutation.mutate(file);
  }

  const atLimit = images.length >= 20;
  const isEmpty = images.length === 0;

  function handleFileDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDropActive(false);
    if (atLimit) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) uploadMutation.mutate(dropped);
  }

  function handleDrop(dropIndex: number) {
    const startIndex = dragIndex.current;
    dragIndex.current = null;
    if (startIndex === null || startIndex === dropIndex) return;

    const next = [...images];
    const [moved] = next.splice(startIndex, 1);
    if (!moved) return;
    next.splice(dropIndex, 0, moved);
    reorderMutation.mutate(next.map((img) => img.file_asset_id));
  }

  const error =
    (uploadMutation.error instanceof Error && uploadMutation.error) ||
    (primaryMutation.error instanceof Error && primaryMutation.error) ||
    (categoryMutation.error instanceof Error && categoryMutation.error) ||
    (deleteMutation.error instanceof Error && deleteMutation.error) ||
    (reorderMutation.error instanceof Error && reorderMutation.error) ||
    null;

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((img, index) => (
            <div
              key={img.file_asset_id}
              data-testid="car-photo-thumb"
              draggable
              onDragStart={() => {
                dragIndex.current = index;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(index);
              }}
              className={`relative flex flex-col gap-1 rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border bg-canvas p-2 ${
                img.is_primary ? 'ring-2 ring-primary border-primary' : 'border-hairline'
              }`}
            >
              <div className="relative">
                {img.is_primary && (
                  <Badge variant="info" className="absolute left-1 top-1 z-10">
                    Primary
                  </Badge>
                )}
                <span
                  data-testid="car-photo-category-badge"
                  className="absolute bottom-1 left-1 z-10"
                >
                  <Badge variant="default">{img.category ?? 'exterior'}</Badge>
                </span>
                <span
                  data-testid="car-photo-drag-handle"
                  aria-hidden="true"
                  className="absolute right-1 top-1 z-10 grid grid-cols-2 gap-0.5 rounded bg-canvas/80 p-1"
                >
                  {Array.from({ length: 4 }).map((_, dotIndex) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size decorative grip, never reordered
                    <span key={dotIndex} className="h-1 w-1 rounded-full bg-steel" />
                  ))}
                </span>
                {brokenIds.has(img.file_asset_id) ? (
                  <div
                    data-testid="car-photo-broken-placeholder"
                    className="flex h-24 w-full items-center justify-center rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-canvas text-xs text-muted"
                  >
                    Image unavailable
                  </div>
                ) : (
                  <img
                    src={img.url}
                    alt="Car"
                    className="h-24 w-full rounded-xl object-cover border border-hairline bg-canvas"
                    onError={() => setBrokenIds((prev) => new Set(prev).add(img.file_asset_id))}
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-1">
                {img.is_primary ? (
                  <span />
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="!rounded-full px-2 py-1 text-xs"
                    disabled={primaryMutation.isPending}
                    onClick={() => primaryMutation.mutate(img.file_asset_id)}
                  >
                    Make primary
                  </Button>
                )}
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  className="!rounded-full px-2 py-1 text-xs"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(img.file_asset_id)}
                >
                  Delete
                </Button>
              </div>
              <label htmlFor={`car_photo_category_${img.file_asset_id}`} className="sr-only">
                Category
              </label>
              <Select
                id={`car_photo_category_${img.file_asset_id}`}
                className="text-xs"
                value={img.category ?? 'exterior'}
                disabled={categoryMutation.isPending}
                onChange={(e) =>
                  categoryMutation.mutate({
                    imageId: img.file_asset_id,
                    category: (e.target as HTMLSelectElement).value,
                  })
                }
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      )}
      <div
        data-testid="car-photo-dropzone"
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDropActive(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setIsDropActive(false)}
        onDrop={handleFileDrop}
        className={`flex flex-wrap items-center gap-4 rounded-md border border-dashed p-4 ${
          atLimit
            ? 'border-hairline bg-canvas'
            : isDropActive
              ? 'border-primary bg-primary/5'
              : 'border-hairline-strong bg-canvas'
        }`}
      >
        <p className="text-sm text-slate">
          {atLimit
            ? 'Maximum of 20 photos reached — delete a photo to add another.'
            : isEmpty
              ? 'Add your first photo — drag an image here, or choose a file.'
              : 'Drag a photo here, or'}
        </p>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="car_photo"
            className={`inline-flex items-center justify-center !rounded-full border border-primary bg-canvas px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 ${
              atLimit ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }`}
          >
            Photo
          </label>
          <input
            id="car_photo"
            type="file"
            accept="image/*"
            disabled={uploadMutation.isPending || atLimit}
            className="sr-only"
            onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!file || uploadMutation.isPending || atLimit}
          loading={uploadMutation.isPending}
          onClick={handleUpload}
        >
          {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
        </Button>
        {uploadMutation.isPending && (
          <p data-testid="car-photo-upload-progress" className="text-sm text-slate">
            Uploading…
          </p>
        )}
        <p className="w-full text-xs text-muted">{images.length} / 20 photos</p>
      </div>
      {error && <p className="text-sm text-red-600">{error.message}</p>}
    </div>
  );
}
